import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ROLES } from "@/lib/api";
import { OfflineStatusBanner } from "@/components/OfflineStatusBanner";
import {
  LayoutDashboard, Package, ClipboardList, Receipt, Wallet,
  Users, ShoppingCart, LogOut, Search, Wrench, PackagePlus,
  Undo2, BarChart3, Archive, History, Menu, X, CloudUpload, UsersRound, Settings as SettingsIcon,
} from "lucide-react";
import BranchSelector from "@/components/BranchSelector";
import NotificationBell from "@/components/NotificationBell";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["admin","manager","accountant","inventory_manager","retail_counter"] },
  { to: "/parts", label: "Parts Catalog", icon: Package, roles: ["admin","manager","accountant","inventory_manager","retail_counter"] },
  { to: "/purchases", label: "Purchases", icon: PackagePlus, roles: ["admin","inventory_manager","manager","accountant"] },
  { to: "/orders", label: "Orders", icon: ShoppingCart, roles: ["admin","manager","retail_counter"] },
  { to: "/billing", label: "Billing", icon: ClipboardList, roles: ["admin","manager","accountant","inventory_manager"] },
  { to: "/invoices", label: "Invoices", icon: Receipt, roles: ["admin","manager","accountant","retail_counter","inventory_manager"] },
  { to: "/returns", label: "Returns", icon: Undo2, roles: ["admin","manager","accountant","retail_counter"] },
  { to: "/payments", label: "Payments", icon: Wallet, roles: ["admin","accountant","manager"] },
  { to: "/reports", label: "Reports", icon: BarChart3, roles: ["admin","accountant","manager","inventory_manager"] },
  { to: "/customers", label: "Customers", icon: UsersRound, roles: ["admin","manager","accountant","inventory_manager","retail_counter"] },
  { to: "/users", label: "User Management", icon: Users, roles: ["admin","manager"] },
  { to: "/audit", label: "Audit & Archive", icon: History, roles: ["admin"] },
  { to: "/sync", label: "Pending Sync", icon: CloudUpload, roles: ["admin","manager","accountant","inventory_manager","retail_counter"] },
  { to: "/settings", label: "Settings", icon: SettingsIcon, roles: ["admin","manager","accountant","inventory_manager","retail_counter"] },
];

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen flex bg-white">
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-zinc-200 flex items-center justify-between px-4 z-40">
        <div className="flex items-center gap-2">
          <button data-testid="mobile-menu-button" onClick={() => setMobileOpen(true)} className="p-2 border border-zinc-300 hover:bg-zinc-50">
            <Menu className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-zinc-950 flex items-center justify-center"><Wrench className="w-3.5 h-3.5 text-white" /></div>
            <span className="font-bold text-sm tracking-tight">PARTSGRID</span>
          </div>
        </div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 flex items-center gap-2">
          <BranchSelector />
          <NotificationBell />
        </div>
      </div>

      {/* Sidebar (mobile overlay + desktop fixed) */}
      {mobileOpen && <div className="lg:hidden fixed inset-0 bg-zinc-950/50 z-40" onClick={() => setMobileOpen(false)} />}
      <aside
        data-testid="sidebar"
        className={`w-64 bg-zinc-50 border-r border-zinc-200 fixed top-0 left-0 h-screen flex flex-col z-50 transition-transform ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
      >
        <div className="h-16 px-5 flex items-center justify-between border-b border-zinc-200">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-zinc-950 flex items-center justify-center">
              <Wrench className="w-4 h-4 text-white" strokeWidth={2} />
            </div>
            <div className="flex flex-col leading-none">
              <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500">System</span>
              <span className="font-bold text-sm text-zinc-950 tracking-tight">PARTSGRID</span>
            </div>
          </div>
          <button className="lg:hidden p-1.5 border border-zinc-300" onClick={() => setMobileOpen(false)}><X className="w-3.5 h-3.5" /></button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4">
          <div className="px-5 mb-2 label-eyebrow">Workspace</div>
          {NAV.filter((n) => n.roles.includes(user.role)).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`sidebar-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-2.5 text-sm border-l-2 transition-colors ${
                  isActive
                    ? "bg-white border-[#002FA7] text-zinc-950 font-medium"
                    : "border-transparent text-zinc-600 hover:bg-white hover:text-zinc-950"
                }`
              }
            >
              <n.icon className="w-4 h-4" strokeWidth={1.5} />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-zinc-200 p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 bg-zinc-950 text-white flex items-center justify-center text-xs font-bold font-mono">
              {user.name?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="flex flex-col leading-tight min-w-0">
              <span className="text-sm font-medium text-zinc-950 truncate" data-testid="current-user-name">{user.name}</span>
              <span className="text-[11px] font-mono uppercase tracking-wider text-zinc-500">{ROLES[user.role]}</span>
            </div>
          </div>
          <button
            data-testid="logout-button"
            onClick={() => { logout(); nav("/login"); }}
            className="w-full flex items-center justify-center gap-2 text-xs uppercase tracking-wider text-zinc-700 hover:text-zinc-950 border border-zinc-300 py-2 hover:bg-zinc-50 transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="lg:ml-64 flex-1 min-h-screen flex flex-col pt-14 lg:pt-0">
        <OfflineStatusBanner />
        <header className="hidden lg:flex h-16 bg-white border-b border-zinc-200 sticky top-0 z-30 items-center justify-between px-8">
          <div className="flex items-center gap-4">
            <span className="label-eyebrow">Logged in as</span>
            <span className="text-sm text-zinc-950 font-medium">{user.email}</span>
          </div>
          <div className="flex items-center gap-3 text-xs font-mono text-zinc-500">
            <BranchSelector />
            <NotificationBell />
            <span className="px-2 py-1 bg-emerald-50 border border-emerald-200 text-emerald-700 uppercase tracking-wider text-[10px] font-semibold">Live</span>
          </div>
        </header>
        <div className="flex-1 p-4 lg:p-8 max-w-[1600px] w-full">{children}</div>
      </main>
    </div>
  );
}
